import React, { useMemo, useRef, useState } from "react";
import axios from "axios";
import contactDefault from "../../data/contact.json";

const backendUrl = process.env.REACT_APP_DATABASEURL;

const makeCaptcha = () => {
  // Avoid ambiguous chars (0/O, 1/I) for readability.
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let out = "";
  for (let i = 0; i < 6; i += 1) {
    out += chars[Math.floor(Math.random() * chars.length)];
  }
  return out;
};

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const mobileRe = /^[6-9]\d{9}$/;

const LeadForm = ({ contact = contactDefault }) => {
  const { leadForm, programs, states, stateCities } = contact;

  const [form, setForm] = useState({
    name: "", email: "", mobile: "", state: "", city: "",
    program: "", course: "", captcha: "", consent: false,
  });
  const [errors, setErrors] = useState({});
  const [captcha, setCaptcha] = useState(makeCaptcha);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null); // { type: 'ok'|'err', text }
  const fieldRefs = useRef({});

  const cities = useMemo(
    () => (form.state && stateCities[form.state]) || [],
    [form.state, stateCities]
  );
  const courses = useMemo(
    () => programs.find((p) => p.label === form.program)?.courses || [],
    [form.program, programs]
  );

  const set = (key, value) => {
    setForm((f) => {
      const next = { ...f, [key]: value };
      if (key === "state") next.city = ""; // reset dependent field
      if (key === "program") next.course = "";
      return next;
    });
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 3) e.name = "Please enter your full name.";
    if (!emailRe.test(form.email)) e.email = "Enter a valid email address.";
    if (!mobileRe.test(form.mobile)) e.mobile = "Enter a valid 10-digit mobile number.";
    if (!form.state) e.state = "Please select your state.";
    if (cities.length > 0 && !form.city) e.city = "Please select your city.";
    if (!form.program) e.program = "Please select a program.";
    if (courses.length > 0 && !form.course) e.course = "Please select a course.";
    if (form.captcha.trim().toUpperCase() !== captcha) e.captcha = "Captcha does not match.";
    if (!form.consent) e.consent = "Please accept to continue.";
    return e;
  };

  const refreshCaptcha = () => {
    setCaptcha(makeCaptcha());
    setForm((f) => ({ ...f, captcha: "" }));
    setErrors((e) => ({ ...e, captcha: undefined }));
  };

  // Captcha input: cap length to the displayed code, normalise to uppercase, and
  // validate live as the user types (error only once the full length is entered).
  const onCaptcha = (value) => {
    const v = value.slice(0, captcha.length).toUpperCase();
    setForm((f) => ({ ...f, captcha: v }));
    setErrors((e) => ({
      ...e,
      captcha: v.length === captcha.length && v !== captcha ? "Captcha does not match." : undefined,
    }));
  };
  const captchaMatched = form.captcha.length === captcha.length && form.captcha === captcha;

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setStatus(null);
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      const first = Object.keys(e)[0];
      fieldRefs.current[first]?.focus();
      return;
    }
    try {
      setLoading(true);
      const res = await axios.post(
        `${backendUrl}api/add_Enquiry_user`,
        {
          name: form.name.trim(),
          email: form.email.trim(),
          mobile_number: form.mobile.trim(),
          state: form.state,
          city: form.city,
          program: form.program,
          course: form.course,
        },
        { headers: { "Content-Type": "application/json" } }
      );
      if (res.status === 200 || res.status === 201) {
        setStatus({ type: "ok", text: "Thank you! Your enquiry has been submitted. Our team will reach out shortly." });
        setForm({ name: "", email: "", mobile: "", state: "", city: "", program: "", course: "", captcha: "", consent: false });
        refreshCaptcha();
      } else {
        setStatus({ type: "err", text: "Something went wrong. Please try again." });
      }
    } catch (err) {
      setStatus({ type: "err", text: "We couldn't submit your enquiry right now. Please try again later." });
    } finally {
      setLoading(false);
    }
  };

  const reg = (key) => (el) => { fieldRefs.current[key] = el; };
  const cls = (key, base) => `${base}${errors[key] ? " is-invalid" : ""}`;

  return (
    <div className="con-card">
      <div className="con-card__head">
        <h2 className="con-card__title">{leadForm.heading}</h2>
        <p className="con-card__sub">{leadForm.subtitle}</p>
      </div>
      <div className="con-card__body">
        <form onSubmit={handleSubmit} noValidate>
          <div className="con-form__grid">
            <div className="con-field con-field--full">
              <label className="con-field__label" htmlFor="lf-name">
                Name<span className="con-field__req">*</span>
              </label>
              <input
                id="lf-name" ref={reg("name")} type="text" autoComplete="name"
                className={cls("name", "con-input")} placeholder="Enter your full name"
                value={form.name} onChange={(e) => set("name", e.target.value)}
                aria-invalid={!!errors.name}
              />
              {errors.name && <span className="con-field__error" role="alert">{errors.name}</span>}
            </div>

            <div className="con-field">
              <label className="con-field__label" htmlFor="lf-email">
                Email Address<span className="con-field__req">*</span>
              </label>
              <input
                id="lf-email" ref={reg("email")} type="email" autoComplete="email"
                className={cls("email", "con-input")} placeholder="you@example.com"
                value={form.email} onChange={(e) => set("email", e.target.value)}
                aria-invalid={!!errors.email}
              />
              {errors.email && <span className="con-field__error" role="alert">{errors.email}</span>}
            </div>

            <div className="con-field">
              <label className="con-field__label" htmlFor="lf-mobile">
                Mobile Number<span className="con-field__req">*</span>
              </label>
              <div className="con-phone">
                <span className="con-phone__cc">+91</span>
                <input
                  id="lf-mobile" ref={reg("mobile")} type="tel" inputMode="numeric" autoComplete="tel"
                  maxLength={10} className={cls("mobile", "con-input")} placeholder="Enter mobile number"
                  value={form.mobile}
                  onChange={(e) => set("mobile", e.target.value.replace(/\D/g, ""))}
                  aria-invalid={!!errors.mobile}
                />
              </div>
              {errors.mobile && <span className="con-field__error" role="alert">{errors.mobile}</span>}
            </div>

            <div className="con-field">
              <label className="con-field__label" htmlFor="lf-state">
                State<span className="con-field__req">*</span>
              </label>
              <div className="con-select-wrap">
                <select
                  id="lf-state" ref={reg("state")} className={cls("state", "con-select")}
                  value={form.state} onChange={(e) => set("state", e.target.value)}
                  aria-invalid={!!errors.state}
                >
                  <option value="">Select State</option>
                  {states.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              {errors.state && <span className="con-field__error" role="alert">{errors.state}</span>}
            </div>

            <div className="con-field">
              <label className="con-field__label" htmlFor="lf-city">
                City{cities.length > 0 && <span className="con-field__req">*</span>}
              </label>
              <div className="con-select-wrap">
                <select
                  id="lf-city" ref={reg("city")} className={cls("city", "con-select")}
                  value={form.city} onChange={(e) => set("city", e.target.value)}
                  disabled={!form.state} aria-invalid={!!errors.city}
                >
                  <option value="">{form.state ? "Select City" : "Select State first"}</option>
                  {cities.map((c) => <option key={c} value={c}>{c}</option>)}
                  {form.state && <option value="Other">Other</option>}
                </select>
              </div>
              {errors.city && <span className="con-field__error" role="alert">{errors.city}</span>}
            </div>

            <div className="con-field">
              <label className="con-field__label" htmlFor="lf-program">
                Program<span className="con-field__req">*</span>
              </label>
              <div className="con-select-wrap">
                <select
                  id="lf-program" ref={reg("program")} className={cls("program", "con-select")}
                  value={form.program} onChange={(e) => set("program", e.target.value)}
                  aria-invalid={!!errors.program}
                >
                  <option value="">Select Program</option>
                  {programs.map((p) => <option key={p.label} value={p.label}>{p.label}</option>)}
                </select>
              </div>
              {errors.program && <span className="con-field__error" role="alert">{errors.program}</span>}
            </div>

            <div className="con-field">
              <label className="con-field__label" htmlFor="lf-course">
                Course{courses.length > 0 && <span className="con-field__req">*</span>}
              </label>
              <div className="con-select-wrap">
                <select
                  id="lf-course" ref={reg("course")} className={cls("course", "con-select")}
                  value={form.course} onChange={(e) => set("course", e.target.value)}
                  disabled={!form.program} aria-invalid={!!errors.course}
                >
                  <option value="">{form.program ? "Select Course" : "Select Program first"}</option>
                  {courses.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              {errors.course && <span className="con-field__error" role="alert">{errors.course}</span>}
            </div>

            <div className="con-field con-field--full">
              <label className="con-field__label" htmlFor="lf-captcha">
                Captcha<span className="con-field__req">*</span>
              </label>
              <div className="con-captcha">
                <span className="con-captcha__box" aria-hidden="true">{captcha}</span>
                <button
                  type="button" className="con-captcha__refresh"
                  onClick={refreshCaptcha} aria-label="Refresh captcha"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16.023 9.348h4.992V4.356M2.985 19.644v-4.992h4.992M19.644 9.348a8.25 8.25 0 00-13.803-3.7L2.985 8.495m18.03 7.01l-2.856 2.847a8.25 8.25 0 01-13.803-3.7" />
                  </svg>
                </button>
                <input
                  id="lf-captcha" ref={reg("captcha")} type="text" autoComplete="off"
                  className={cls("captcha", "con-input")} placeholder="Enter captcha"
                  style={{ flex: 1 }} value={form.captcha}
                  maxLength={captcha.length}
                  inputMode="text"
                  onChange={(e) => onCaptcha(e.target.value)}
                  aria-invalid={!!errors.captcha}
                />
              </div>
              {errors.captcha ? (
                <span className="con-field__error" role="alert">{errors.captcha}</span>
              ) : captchaMatched ? (
                <span className="con-captcha__ok" role="status">✓ Captcha matched</span>
              ) : null}
            </div>
          </div>

          <div className="con-consent">
            <input
              id="lf-consent" ref={reg("consent")} type="checkbox"
              checked={form.consent} onChange={(e) => set("consent", e.target.checked)}
            />
            <label htmlFor="lf-consent">
              {leadForm.consent}<span className="con-field__req">*</span>
            </label>
          </div>
          {errors.consent && <span className="con-field__error" role="alert">{errors.consent}</span>}

          <div className="con-submit-row">
            <button type="submit" className="btn-ed btn-ed--primary con-submit" disabled={loading}>
              {loading && <span className="con-spinner" aria-hidden="true" />}
              {loading ? "Submitting…" : "Submit"}
            </button>
          </div>

          {status && (
            <div className={`con-status con-status--${status.type === "ok" ? "ok" : "err"}`} role="status" aria-live="polite">
              {status.text}
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default LeadForm;
